import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Download, ChefHat, AlertTriangle } from "lucide-react";
import { printInventorySnapshot } from "@/lib/inventoryPdf";
import { Button } from "@/components/ui/button";

// Public, unauthenticated read-only view of an inventory snapshot.
// Users can share this link with owners, suppliers, insurance, or investors.

function formatMoney(v, currency = "USD") {
  try {
    return v.toLocaleString(undefined, { style: "currency", currency, maximumFractionDigits: 2 });
  } catch {
    return `${currency} ${(Number(v) || 0).toFixed(2)}`;
  }
}

export default function PublicInventoryShare() {
  const { token } = useParams();
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/inventory/share/${token}`);
        setSnap(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Snapshot not found.");
      }
    })();
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 text-center bg-background">
        <div>
          <div className="font-display text-3xl mb-2">Snapshot unavailable.</div>
          <p className="text-muted-foreground text-sm">{err}</p>
          <Link to="/" className="text-xs gold hover:brightness-110 mt-4 inline-block">Back to Culinaire Specialist →</Link>
        </div>
      </div>
    );
  }
  if (!snap) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const currency = snap.currency || "USD";
  const date = snap.generated_at ? new Date(snap.generated_at) : new Date();

  return (
    <div className="min-h-screen bg-background" data-testid="public-inventory-share">
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Header brand strip */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <Link to="/" className="flex items-center gap-2 text-xs uppercase tracking-[0.28em]" style={{ color: "#c8a96a" }}>
            <ChefHat className="w-3.5 h-3.5" />
            Culinaire Specialist • Operations
          </Link>
          <Button size="sm" variant="outline" onClick={() => printInventorySnapshot(snap)} className="gap-2" data-testid="public-share-print">
            <Download className="w-4 h-4" /> Save as PDF
          </Button>
        </div>

        <div className="label-eyebrow">Inventory snapshot</div>
        <h1 className="font-display text-3xl md:text-4xl mt-2 leading-tight">
          {snap.restaurant_name || "Kitchen"}
        </h1>
        <div className="text-xs text-muted-foreground mt-2">
          Snapshot dated {date.toLocaleString()} • {snap.item_count} priced ingredients
        </div>

        {/* Total value hero */}
        <div
          className="mt-8 rounded-2xl p-6 md:p-8"
          style={{
            background: "linear-gradient(180deg, rgba(232,207,154,0.10), rgba(232,207,154,0.02))",
            border: "1px solid rgba(232,207,154,0.35)",
          }}
          data-testid="public-share-total"
        >
          <div className="label-eyebrow" style={{ color: "rgba(232,207,154,0.85)" }}>Total inventory value</div>
          <div className="font-display text-5xl md:text-6xl tracking-tight mt-2" style={{ color: "#f5efe0" }}>
            {formatMoney(snap.total_inventory_value || 0, currency)}
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            Across {snap.item_count} priced ingredients • currency {currency}
          </div>
        </div>

        {/* Low stock */}
        <div className="mt-8" data-testid="public-share-low-stock">
          <div className="label-eyebrow flex items-center gap-2 text-destructive/90 mb-3">
            <AlertTriangle className="w-3 h-3" /> Below minimum stock • {snap.low_stock_count}
          </div>
          <div className="rounded-2xl overflow-x-auto border border-destructive/40 bg-destructive/5">
            <div className="min-w-[560px]">
            <div className="grid grid-cols-12 px-4 py-2 text-[0.6rem] uppercase tracking-wider text-muted-foreground border-b border-destructive/20 bg-destructive/10">
              <div className="col-span-5">Ingredient</div>
              <div className="col-span-2 text-right">On hand</div>
              <div className="col-span-2 text-right">Min level</div>
              <div className="col-span-3 text-right">Suggested order</div>
            </div>
            {(snap.low_stock || []).length === 0 ? (
              <div className="px-4 py-6 text-sm text-muted-foreground text-center">
                Every item is at or above its minimum stock level.
              </div>
            ) : (
              (snap.low_stock || []).map((r) => (
                <div key={r.ingredient_id} className="grid grid-cols-12 px-4 py-2 text-sm border-b border-destructive/15 last:border-b-0 items-center">
                  <div className="col-span-5 truncate">{r.name}</div>
                  <div className="col-span-2 text-right tabular text-muted-foreground">{r.on_hand} {r.purchase_unit}</div>
                  <div className="col-span-2 text-right tabular text-muted-foreground">{r.par_level} {r.purchase_unit}</div>
                  <div className="col-span-3 text-right tabular font-semibold text-destructive">
                    {r.suggested_order} {r.purchase_unit}
                  </div>
                </div>
              ))
            )}
            </div>
          </div>
        </div>

        {/* Full list */}
        {snap.items?.length > 0 && (
          <div className="mt-8">
            <div className="label-eyebrow mb-3">Full inventory</div>
            <div className="rounded-2xl glass-card overflow-x-auto">
              <div className="min-w-[640px]">
              <div className="grid grid-cols-12 px-4 py-2 text-[0.6rem] uppercase tracking-wider text-muted-foreground border-b border-border bg-secondary/40">
                <div className="col-span-5">Ingredient</div>
                <div className="col-span-2 text-right">Category</div>
                <div className="col-span-2 text-right">On hand</div>
                <div className="col-span-1 text-right">Min</div>
                <div className="col-span-2 text-right">Value</div>
              </div>
              {snap.items.map((r) => (
                <div key={r.ingredient_id} className={`grid grid-cols-12 px-4 py-2 text-sm border-b border-border/40 last:border-b-0 items-center ${r.below_par ? "bg-destructive/5" : ""}`}>
                  <div className="col-span-5 truncate flex items-center gap-2">
                    <span>{r.name}</span>
                    {r.below_par && <span className="text-[0.5rem] px-1.5 py-0.5 rounded-full bg-destructive text-destructive-foreground font-bold tracking-wider">LOW</span>}
                  </div>
                  <div className="col-span-2 text-right text-xs text-muted-foreground capitalize">{r.category || "-"}</div>
                  <div className="col-span-2 text-right tabular">{r.on_hand} <span className="text-muted-foreground text-xs">{r.purchase_unit}</span></div>
                  <div className="col-span-1 text-right tabular text-muted-foreground text-xs">{r.par_level || "-"}</div>
                  <div className="col-span-2 text-right tabular">{formatMoney(r.est_value || 0, currency)}</div>
                </div>
              ))}
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-border text-xs text-muted-foreground flex items-center justify-between flex-wrap gap-3">
          <span>Prepared by <span className="text-foreground">{snap.generated_by?.name || snap.restaurant_name}</span></span>
          <Link to="/" className="gold hover:brightness-110">Culinaire Specialist • Precision culinary operations →</Link>
        </div>
      </div>
    </div>
  );
}
